import { useState } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Zap, Clock, MapPin, Activity } from 'lucide-react';
import { useMapZoom } from './MapBearingContext';
import { RegulatorState } from '../../types/protocol';
import type { CabinetDefinition } from '../../types/intersection';
import type { IntersectionTrafficData } from '../../types/traffic';

interface CabinetMarkerProps {
  cabinet: CabinetDefinition;
  state: RegulatorState;
  isConnected: boolean;
  currentPlan: number;
  currentPhase: string;
  cyclePosition: number;
  cycleTime: number;
  groupCount: number;
  detectorCount: number;
  trafficData?: IntersectionTrafficData;
  alarmCount?: number;
  forceOpen?: boolean;
}

const stateColors: Record<RegulatorState, { border: string; glow: string; label: string }> = {
  [RegulatorState.DISCONNECTED]:  { border: '#6b7280', glow: 'rgba(107,114,128,0.3)', label: 'Desconectado' },
  [RegulatorState.OFF]:           { border: '#374151', glow: 'rgba(55,65,81,0.3)',     label: 'Apagado' },
  [RegulatorState.FLASHING]:      { border: '#f59e0b', glow: 'rgba(245,158,11,0.4)',   label: 'Intermitente' },
  [RegulatorState.LOCAL_CONTROL]: { border: '#3b82f6', glow: 'rgba(59,130,246,0.4)',   label: 'Control Local' },
  [RegulatorState.DEMAND_CONTROL]:{ border: '#a855f7', glow: 'rgba(168,85,247,0.4)',   label: 'Por Demanda' },
  [RegulatorState.REMOTE_CONTROL]:{ border: '#22c55e', glow: 'rgba(34,197,94,0.4)',    label: 'Centralizado' },
  [RegulatorState.PHASE_END]:     { border: '#06b6d4', glow: 'rgba(6,182,212,0.4)',    label: 'Fin de Fase' },
};

function formatTime(tenths: number): string {
  const s = Math.floor(tenths / 10);
  const t = tenths % 10;
  return `${s}.${t}s`;
}

export function CabinetMarker({
  cabinet,
  state,
  isConnected,
  currentPlan,
  currentPhase,
  cyclePosition,
  cycleTime,
  groupCount,
  detectorCount,
  trafficData,
  alarmCount = 0,
  forceOpen = false,
}: CabinetMarkerProps) {
  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;
  const mapZoom = useMapZoom();
  const showDetailed = mapZoom >= 17;
  const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));
  const zoneSize = Math.min(120, Math.max(28, (17 - mapZoom) * 22 + 28));
  const hasAlarms = alarmCount > 0;
  const colors = stateColors[state];
  const isFlashing = state === RegulatorState.FLASHING;

  return (
    <Marker
      longitude={cabinet.position[0]}
      latitude={cabinet.position[1]}
      anchor={showDetailed ? 'bottom' : 'center'}
    >
      <div className="flex flex-col items-center" style={showDetailed ? { transform: `scale(${scale})` } : undefined}>
        {/* Popup info */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-full mb-2 w-56 bg-gray-900/95 backdrop-blur-md border border-gray-600/50 rounded-lg shadow-2xl overflow-hidden z-50"
            >
              {/* Header */}
              <div className="px-3 py-2 border-b border-gray-700/50 flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs text-white">ID: {cabinet.logicalId}</div>
                  <div className="font-mono text-[10px] text-gray-500">{cabinet.physicalId}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5 text-gray-500" />
                    <span className="font-mono text-[10px] text-gray-500">
                      {cabinet.position[1].toFixed(5)}, {cabinet.position[0].toFixed(5)}
                    </span>
                  </div>
                </div>
                {isConnected ? (
                  <Wifi className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5 text-red-400" />
                )}
              </div>

              {/* Estado */}
              <div className="px-3 py-2 border-b border-gray-700/50">
                <motion.div
                  className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
                  style={{ backgroundColor: colors.border }}
                  animate={isFlashing ? { opacity: [1, 0.4, 1] } : {}}
                  transition={isFlashing ? { duration: 1, repeat: Infinity } : {}}
                >
                  {colors.label}
                </motion.div>
              </div>

              {/* Plan + Fase */}
              <div className="px-3 py-2 grid grid-cols-2 gap-2 border-b border-gray-700/50">
                <div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    <Zap className="w-2.5 h-2.5" /> Plan
                  </div>
                  <div className="font-mono text-sm text-white">
                    {currentPlan === 0 ? 'EXT' : currentPlan}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> Fase
                  </div>
                  <div className="font-mono text-sm text-white">{currentPhase}</div>
                </div>
              </div>

              {/* Ciclo */}
              {cycleTime > 0 && (
                <div className="px-3 py-2 border-b border-gray-700/50">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Ciclo</span>
                    <span>{formatTime(cyclePosition)} / {formatTime(cycleTime)}</span>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: colors.border }}
                      animate={{ width: `${(cyclePosition / cycleTime) * 100}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                </div>
              )}

              {/* Resumen */}
              <div className="px-3 py-1.5 flex justify-between text-[10px] text-gray-500 border-b border-gray-700/50">
                <span>{groupCount} grupos</span>
                <span>{detectorCount} detectores</span>
              </div>

              {/* Tráfico en tiempo real */}
              {trafficData && trafficData.segments.length > 0 && (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-1 mb-1.5">
                    <Activity className="w-2.5 h-2.5 text-cyan-400" />
                    <span className="text-[10px] text-gray-400">Tráfico en tiempo real</span>
                  </div>
                  <div className="space-y-1">
                    {trafficData.segments.map(seg => {
                      const dotColor =
                        seg.roadClosure ? '#ef4444' :
                        seg.congestionRatio > 0.75 ? '#22c55e' :
                        seg.congestionRatio > 0.5  ? '#f59e0b' :
                        seg.congestionRatio > 0.25 ? '#f97316' : '#ef4444';
                      return (
                        <div key={seg.accessId} className="flex items-center justify-between text-[10px]">
                          <span className="text-gray-400 truncate mr-2">{seg.label}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-white">
                              {Math.round(seg.currentSpeed)}/{Math.round(seg.freeFlowSpeed)}
                            </span>
                            <span className="text-gray-500">km/h</span>
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: dotColor }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {trafficData.lastUpdate && (
                    <div className="text-[9px] text-gray-600 mt-1.5">
                      {trafficData.lastUpdate.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {mapZoom >= 17 ? (
          <>
            {/* Gabinete visual (zoom alto) */}
            <motion.div
              onClick={() => setOpen(o => !o)}
              className="relative cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Glow */}
              <motion.div
                className="absolute -inset-1.5 rounded-md"
                style={{ backgroundColor: colors.glow }}
                animate={isConnected ? { opacity: [0.4, 0.8, 0.4] } : { opacity: 0.2 }}
                transition={isConnected ? { duration: 2, repeat: Infinity } : {}}
              />

              {/* Caja del gabinete */}
              <motion.div
                className="relative w-6 h-8 rounded-sm flex flex-col items-center justify-center gap-0.5"
                style={{
                  backgroundColor: '#1f2937',
                  border: `2px solid ${hasAlarms ? '#ef4444' : colors.border}`,
                  boxShadow: hasAlarms
                    ? '0 0 8px rgba(239,68,68,0.5)'
                    : `0 0 6px ${colors.glow}`,
                }}
                animate={hasAlarms ? { boxShadow: ['0 0 4px rgba(239,68,68,0.3)', '0 0 12px rgba(239,68,68,0.6)', '0 0 4px rgba(239,68,68,0.3)'] } : {}}
                transition={hasAlarms ? { duration: 1.5, repeat: Infinity } : {}}
              >
                {/* LEDs de estado */}
                <motion.div
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: isConnected ? '#22c55e' : '#ef4444' }}
                  animate={isConnected ? { opacity: [1, 0.3, 1] } : {}}
                  transition={isConnected ? { duration: 1.5, repeat: Infinity } : {}}
                />
                <div className="w-3 h-0.5 rounded-full bg-gray-600" />
                <div className="w-3 h-0.5 rounded-full bg-gray-600" />
              </motion.div>

              {/* Antena */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-[1px] h-2 bg-gray-400" />
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-400" />

              {/* Alarm badge */}
              {hasAlarms && (
                <motion.div
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] rounded-full bg-red-500 flex items-center justify-center"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <span className="text-[8px] font-bold text-white leading-none">{alarmCount}</span>
                </motion.div>
              )}
            </motion.div>

            {/* Sombra */}
            <div className="w-4 h-1 mt-0.5 rounded-full bg-black/25 blur-[1px]" />
          </>
        ) : (
          <motion.div
            onClick={() => setOpen(o => !o)}
            className="relative cursor-pointer flex items-center justify-center"
            style={{ width: zoneSize, height: zoneSize }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Outer pulsing ring */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                border: `2px solid ${colors.border}`,
                backgroundColor: colors.glow,
              }}
              animate={isConnected ? { opacity: [0.25, 0.5, 0.25], scale: [1, 1.08, 1] } : { opacity: 0.15 }}
              transition={isConnected ? { duration: 3, repeat: Infinity, ease: 'easeInOut' } : {}}
            />
            {/* Inner solid dot */}
            <div
              className="relative w-3 h-3 rounded-full z-10"
              style={{
                backgroundColor: colors.border,
                boxShadow: `0 0 8px ${colors.glow}`,
              }}
            />
            {/* Label */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-gray-900/80 text-gray-300">
                R{cabinet.logicalId}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </Marker>
  );
}
